'use client';

import isEqual from 'lodash.isequal';
import { usePathname, useSearchParams } from 'next/navigation';
import { useContext, useEffect } from 'react';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import RouteContext from '~/context/srp/RouteContext';
import decodeUrlFilters from '~/utilities/decodeUrlFilters';
import reloadWindow from '~/utilities/reloadWindow';

export default function useNavigation() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const {
    appliedFilters,
    incrementPopstateCount,
    pageNumber,
    setAppliedFilters,
    setPageNumber,
    setSortBy,
    sortBy
  } = useContext(AppliedFiltersContext);
  const { setCurrentUrl } = useContext(RouteContext);

  // Back-navigation state restoration is handled in AppliedFiltersContext's
  // useState initialization (consumeBackNavUrl) — not here. useLayoutEffect
  // cannot be used because useSubsequentEffect in useShouldFetch treats the
  // layout-effect-triggered re-render as "first mount" and skips the request
  // change, preventing inventory from re-fetching.

  // Listen for browser back/forward navigation
  useEffect(() => {
    let prevUrl = window.location.href;

    function handleNavigation(_: PopStateEvent) {
      const currentUrl = window.location.href;
      const urlChanged = currentUrl !== prevUrl;
      prevUrl = currentUrl;

      const url = new URL(currentUrl);

      // If popstate navigates away from SRP (e.g., forward button to VDP),
      // don't decode the non-SRP URL as filter state — SRP is about to unmount.
      // State restoration on back-nav is handled by window.history.state.srp
      // which useSyncUrl embeds in every history entry.
      if (urlChanged && !url.pathname.startsWith('/cars')) {
        return;
      }

      const {
        filters: newFilters,
        isVanityPath,
        page: newPageNumber,
        sortBy: newSortOption
      } = decodeUrlFilters(url);

      if (isVanityPath) {
        reloadWindow();

        return;
      }

      setPageNumber.call(null, newPageNumber);
      setAppliedFilters.call(null, newFilters);
      setSortBy.call(null, newSortOption);

      // If the URL changed but all decoded values already match the current
      // context state, the setters above will bail out via deep-equality and
      // request will not change, preventing an inventory re-fetch. Force one
      // only in this edge case — normal back-nav where at least one value
      // differs triggers a natural re-fetch without needing the counter.
      if (urlChanged) {
        const allSettersBailedOut =
          isEqual(appliedFilters, newFilters) &&
          pageNumber === newPageNumber &&
          sortBy === newSortOption;

        if (allSettersBailedOut) {
          incrementPopstateCount();
        }
      }
    }

    window.addEventListener('popstate', handleNavigation);

    return () => {
      window.removeEventListener('popstate', handleNavigation);
    };
  }, [
    appliedFilters,
    incrementPopstateCount,
    pageNumber,
    setAppliedFilters,
    setPageNumber,
    setSortBy,
    sortBy
  ]);

  // Update RouteContext when pathname or searchParams change
  useEffect(() => {
    const params = searchParams?.toString();
    const url = pathname + (params ? `?${params}` : '');
    setCurrentUrl(url.toLowerCase());
  }, [pathname, searchParams, setCurrentUrl]);
}
